import { useTranslation } from 'react-i18next'
import type { useAiChatLiveState } from './ai-chat-live-state'
import { Button } from '@/shared/ui/button'

export function AiChatHistory({
  state,
}: {
  state: ReturnType<typeof useAiChatLiveState>
}) {
  const { t } = useTranslation('aiChat')
  const { history } = state
  const error = state.errorMessage ?? history.errorMessage

  return (
    <section
      aria-label={t('history')}
      className="min-h-0 flex-1 overflow-y-auto p-4"
    >
      <h3 className="mb-4 font-medium">{t('history')}</h3>
      <HistoryError
        message={error}
        onRetry={history.errorMessage ? history.retry : undefined}
        loading={history.loading}
      />
      <HistoryStatus history={history} error={error} />
      <ul className="space-y-2">
        {history.sessions.map((session) => (
          <HistoryRow
            key={session.id}
            session={session}
            current={session.id === state.activeSessionId}
            disabled={state.blocked || history.loading}
            onSelect={state.selectConversation}
          />
        ))}
      </ul>
      {history.canLoadMore ? (
        <Button
          className="mt-4"
          variant="outline"
          disabled={history.loading || history.loadingMore}
          onClick={history.loadMore}
        >
          {t(history.loadingMore ? 'historyLoading' : 'loadMore')}
        </Button>
      ) : null}
    </section>
  )
}

function HistoryError({
  message,
  onRetry,
  loading,
}: {
  message?: string
  onRetry?: () => void
  loading: boolean
}) {
  const { t } = useTranslation('aiChat')
  if (!message) return null

  return (
    <div role="alert" className="mb-4 space-y-2">
      <p className="text-sm text-destructive">{message}</p>
      {onRetry ? (
        <Button
          variant="outline"
          size="sm"
          disabled={loading}
          onClick={onRetry}
        >
          {t('retry')}
        </Button>
      ) : null}
    </div>
  )
}

function HistoryRow({
  session,
  current,
  disabled,
  onSelect,
}: {
  session: ReturnType<typeof useAiChatLiveState>['history']['sessions'][number]
  current: boolean
  disabled: boolean
  onSelect: (id: string) => void
}) {
  const { t, i18n } = useTranslation('aiChat')

  return (
    <li>
      <button
        type="button"
        disabled={disabled}
        aria-current={current ? 'true' : undefined}
        onClick={() => onSelect(session.id)}
        className="w-full rounded-md border p-3 text-left hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
      >
        <span className="block break-words font-medium">{session.title}</span>
        <span className="mt-1 flex flex-wrap justify-between gap-2 text-xs text-muted-foreground">
          <time dateTime={session.updatedAt}>
            {new Date(session.updatedAt).toLocaleString(i18n.language)}
          </time>
          {current ? <span>{t('currentConversation')}</span> : null}
        </span>
      </button>
    </li>
  )
}

function HistoryStatus({
  history,
  error,
}: {
  history: ReturnType<typeof useAiChatLiveState>['history']
  error?: string
}) {
  const { t } = useTranslation('aiChat')

  return (
    <>
      {' '}
      {history.loading && !history.loadingMore ? (
        <p role="status" className="text-sm text-muted-foreground">
          {t('historyLoading')}
        </p>
      ) : null}
      {!history.loading && !error && !history.sessions.length ? (
        <p className="text-sm text-muted-foreground">{t('historyEmpty')}</p>
      ) : null}
    </>
  )
}
